// Reconstructs desktop-Word's `mso-list` paragraph runs into real
// <ol>/<ul><li aria-level> markup BEFORE `sanitizeOfficeHtml` strips the two
// things a desktop-Word list is made of:
//   - the `mso-list:lN levelM lfoK` inline style (sanitizeOfficeHtml.ts:39)
//   - the `<![if !supportLists]>…<![endif]>` ordinal marker (sanitizeOfficeHtml.ts:28)
//
// Desktop Word (unlike Word-for-web/Google Docs) never emits real <ol>/<ul>
// tags — every list item is a flat <p class=MsoListParagraph> carrying
// mso-list plus a fake bullet/number rendered as literal text inside the
// conditional block. Left alone, sanitizeOfficeHtml deletes both signals and
// the paste pipeline sees plain, unnumbered paragraphs.
//
// Output matches the flat shape `enrichListAtDepth` (pasteNormalize.ts)
// already consumes for Word-for-web: one <ol>/<ul> per run of consecutive
// same-list paragraphs, each <li> carrying its own `aria-level` (Word's
// `level` component) and `style="list-style-type:…"` classified from the
// marker text/font.

const MSO_LIST_RE = /mso-list\s*:\s*(l\d+)(?:\s+level(\d+))?/i;

type WordListStyleType = 'decimal' | 'lower-roman' | 'upper-roman' | 'lower-alpha' | 'upper-alpha' | 'disc';

const ORDERED_STYLES = new Set<WordListStyleType>(['decimal', 'lower-roman', 'upper-roman', 'lower-alpha', 'upper-alpha']);

// Word's "Define New Number Format" can wrap the ordinal in parens/brackets
// instead of the plain trailing `.`/`)` — `(1)`, `1]`, `[1]`, `(a)`, `(i)` are
// all still ORDERED markers, just a different bracketing convention.
const ORDERED_DECIMAL_RE = /^[(\[]?\d+[.)\]]/;
// Full Roman alphabet (i v x l c d m) so multi-letter markers like `xl.` / `li.`
// classify as roman and keep their start value, matching romanToInt's coverage.
// (The level>=3 roman-over-alpha preference below already applied to i/v/x.)
const ORDERED_ROMAN_RE = /^[(\[]?([ivxlcdmIVXLCDM]+)[.)\]]/;
const ORDERED_ALPHA_RE = /^[(\[]?([a-zA-Z])[.)\]]/;

// Recognized bullet glyphs (·, o, §, ▪) — anything else unclassifiable is an
// ORDERED marker we simply don't recognize, not a bullet.
const BULLET_GLYPH_RE = /[·§▪]|^o$/;
const BULLET_FONT_RE = /symbol|wingdings/i;

function classifyMarker(markerText: string, level: number, isBulletFont: boolean): WordListStyleType {
    const normalized = markerText.trim();
    if (ORDERED_DECIMAL_RE.test(normalized)) return 'decimal';
    if (level >= 3) {
        const roman = ORDERED_ROMAN_RE.exec(normalized);
        if (roman) return /[A-Z]/.test(roman[1]) ? 'upper-roman' : 'lower-roman';
    }
    const alpha = ORDERED_ALPHA_RE.exec(normalized);
    if (alpha) return /[A-Z]/.test(alpha[1]) ? 'upper-alpha' : 'lower-alpha';
    // A recognized bullet glyph, or a Symbol/Wingdings marker font (Word's own
    // bullet-rendering signal), is a real bullet — keep it disc. Anything else
    // unclassifiable/empty is an ORDERED marker we don't recognize (a custom
    // Define-New-Number-Format glyph, a non-Latin ordinal, …) — default to
    // decimal, the safe direction: an unrecognized marker is far more likely a
    // number we failed to parse than a bullet, and demoting a numbered list to
    // bullets drops ordering the reader relies on, whereas the reverse is benign.
    if (isBulletFont || BULLET_GLYPH_RE.test(normalized)) return 'disc';
    return 'decimal';
}

function romanToInt(s: string): number {
    const map: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
    const lower = s.toLowerCase();
    let total = 0;
    for (let i = 0; i < lower.length; i++) {
        const cur = map[lower[i]];
        if (cur == null) return NaN;
        const next = map[lower[i + 1]];
        total += next && cur < next ? -cur : cur;
    }
    return total;
}

// The numeric start index a reconstructed <ol> should carry, parsed from the
// captured Word marker ("3." → 3, "c." → 3, "iii." → 3). Word emits the real
// ordinal in the `<![if !supportLists]>` payload, so a list copied from the
// middle (starting at 3) must not restart at 1. Returns undefined when the
// marker has no parseable ordinal (bullets, unrecognized glyphs).
function markerStartOrdinal(text: string, type: WordListStyleType): number | undefined {
    const normalized = text.trim();
    if (type === 'decimal') {
        const m = /^[(\[]?(\d+)/.exec(normalized);
        return m ? parseInt(m[1], 10) : undefined;
    }
    if (type === 'lower-alpha' || type === 'upper-alpha') {
        const m = ORDERED_ALPHA_RE.exec(normalized);
        if (!m) return undefined;
        const n = m[1].toLowerCase().charCodeAt(0) - 96; // a → 1
        return n >= 1 && n <= 26 ? n : undefined;
    }
    if (type === 'lower-roman' || type === 'upper-roman') {
        const m = ORDERED_ROMAN_RE.exec(normalized);
        if (!m) return undefined;
        const n = romanToInt(m[1]);
        return Number.isFinite(n) && n > 0 ? n : undefined;
    }
    return undefined;
}

interface OrdinalMarker {
    text: string;
    isBulletFont: boolean;
}

// Extract + strip the `<![if !supportLists]>…<![endif]>` ordinal payload from
// a list paragraph's children. Browsers parse those IE-only conditional
// markers as bogus HTML comments (not real comments), so the wrapped marker
// nodes are ordinary DOM children sitting between two Comment nodes whose
// data is `[if !supportLists]` / `[endif]`. Mutates `block` to remove the
// marker nodes, leaving only the item's real content behind.
function extractOrdinalMarker(block: HTMLElement): OrdinalMarker {
    const children = Array.from(block.childNodes);
    const isSupportListsStart = (n: ChildNode) => n.nodeType === Node.COMMENT_NODE && /^\[if\b.*!supportLists/i.test((n as Comment).data);
    const isEndIf = (n: ChildNode) => n.nodeType === Node.COMMENT_NODE && /^\[endif\]?$/i.test((n as Comment).data.trim());

    const startIdx = children.findIndex(isSupportListsStart);
    if (startIdx === -1) return { text: '', isBulletFont: false };
    const endIdx = children.findIndex((n, i) => i > startIdx && isEndIf(n));
    if (endIdx === -1) return { text: '', isBulletFont: false };

    const markerNodes = children.slice(startIdx + 1, endIdx);
    const text = markerNodes.map((n) => n.textContent ?? '').join('');
    // A Symbol/Wingdings marker font is Word's own bullet-rendering signal —
    // scan the marker's own markup (covers nested spans) rather than the
    // stripped text, which loses the font entirely.
    const markerHtml = markerNodes.map((n) => (n instanceof Element ? n.outerHTML : '')).join('');
    const isBulletFont = BULLET_FONT_RE.test(markerHtml);
    for (const node of children.slice(startIdx, endIdx + 1)) block.removeChild(node);
    return { text, isBulletFont };
}

interface ListRunItem {
    level: number;
    type: WordListStyleType;
    block: HTMLElement;
    start?: number;
}

function flushRun(doc: Document, items: ListRunItem[]): void {
    if (!items.length) return;
    const wrapperTag = ORDERED_STYLES.has(items[0].type) ? 'ol' : 'ul';
    const wrapper = doc.createElement(wrapperTag);
    wrapper.setAttribute('style', `list-style-type:${items[0].type}`);
    // Preserve the run's starting ordinal so a list copied from the middle (3., 4.,
    // …) doesn't restart at 1. Omit start="1" to keep byte-identical output for the
    // common case.
    const runStart = items[0].start;
    if (wrapperTag === 'ol' && runStart != null && runStart > 1) {
        wrapper.setAttribute('start', String(runStart));
    }
    for (const item of items) {
        const li = doc.createElement('li');
        li.setAttribute('aria-level', String(item.level));
        li.setAttribute('style', `list-style-type:${item.type}`);
        while (item.block.firstChild) li.appendChild(item.block.firstChild);
        wrapper.appendChild(li);
    }
    // Insert within the run's OWN parent (replaceWith is parent-relative), so
    // a list nested inside a <div class=WordSection1> or a <td> stays put.
    items[0].block.replaceWith(wrapper);
    for (let i = 1; i < items.length; i++) items[i].block.remove();
}

// True when `candidate` immediately follows `prev` among its parent's
// children, skipping only whitespace-only text nodes (pretty-printed
// indentation between paragraphs). Anything else in between — a non-list
// paragraph, an element, meaningful text — breaks the run.
function isAdjacentListItem(prev: HTMLElement, candidate: HTMLElement): boolean {
    let node: Node | null = prev.nextSibling;
    while (node) {
        if (node === candidate) return true;
        if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() === '') {
            node = node.nextSibling;
            continue;
        }
        return false;
    }
    return false;
}

// The DOM parse→innerHTML round-trip below rewrites every OTHER Word
// `<![if …]>…<![endif]>` conditional range (table spacer rows, empty-paragraph
// padding, page-break hints — everything but the list ordinal already handled
// by extractOrdinalMarker) from the bogus-comment form the browser parses
// `<![if]>` as into a pair of ORDINARY standalone comments,
// `<!--[if …]-->…<!--[endif]-->`. sanitizeOfficeHtml.ts:28 only recognizes the
// original `<![if]>` syntax, so by the time it runs, the payload between the
// two comments has become live, visible content instead of an invisible
// conditional block — a `<tr height=0>` spacer row leaks into a pasted table,
// `&nbsp;`-only paragraphs leak in as blank lines, stray glyphs leak as text.
// Strip every such range ourselves, walking the live DOM (which still knows
// the true boundaries) instead of relying on a string regex that can no
// longer find them once the round-trip has happened.
//
// A sibling-only walk is NOT enough: a `<tr>` inside the payload triggers
// HTML5's table foster-parenting, which auto-inserts a <tbody> and moves the
// row (and the closing `[endif]` comment right after it) a level deeper than
// the opening `[if]` comment — the two ends of the SAME range end up at
// different tree depths. `Range.deleteContents()` handles that correctly
// (it removes across depths via the nearest common ancestor), so pairing is
// done by DOCUMENT ORDER over every comment node, not sibling adjacency.
function stripConditionalRanges(root: HTMLElement): void {
    const isIfComment = (n: Node) => n.nodeType === Node.COMMENT_NODE && /^\s*\[if\b/i.test((n as Comment).data);
    const isEndIfComment = (n: Node) => n.nodeType === Node.COMMENT_NODE && /^\s*\[endif\]/i.test((n as Comment).data);

    // Snapshot every comment node BEFORE removing anything — mutating the
    // tree mid-walk would leave the live TreeWalker's position undefined.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    const comments: Comment[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) comments.push(n as Comment);

    const doc = root.ownerDocument;
    let pendingStart: Comment | null = null;
    for (const comment of comments) {
        if (isIfComment(comment)) {
            pendingStart?.parentNode?.removeChild(pendingStart); // unmatched — drop alone
            pendingStart = comment;
        } else if (isEndIfComment(comment)) {
            if (pendingStart) {
                const range = doc.createRange();
                range.setStartBefore(pendingStart);
                range.setEndAfter(comment);
                range.deleteContents();
                pendingStart = null;
            } else {
                comment.parentNode?.removeChild(comment); // unmatched — no preceding [if]
            }
        }
    }
    pendingStart?.parentNode?.removeChild(pendingStart);
}

export function reconstructWordMsoLists(html: string): string {
    if (!/mso-list\s*:/i.test(html)) return html;
    if (typeof DOMParser === 'undefined') return html;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.body;
        if (!body) return html;

        // Real Word clipboard HTML wraps paragraphs in <div class=WordSection1>,
        // and lists can sit inside table cells — scan the WHOLE document for
        // list paragraphs (not just body's direct children) and group them by
        // parent + sibling adjacency, so a run's wrapper is inserted exactly
        // where the run lived, however deep it was nested.
        const listParas = Array.from(body.querySelectorAll('[style*="mso-list"]')) as HTMLElement[];

        let run: ListRunItem[] = [];
        let runListId: string | null = null;
        let runParent: Element | null = null;
        let prevBlock: HTMLElement | null = null;

        const flush = () => {
            flushRun(doc, run);
            run = [];
            runListId = null;
            runParent = null;
            prevBlock = null;
        };

        for (const block of listParas) {
            const match = MSO_LIST_RE.exec(block.getAttribute('style') ?? '');
            if (!match) continue;
            const [, listId, levelStr] = match;
            const parent = block.parentElement;
            const continuesRun =
                run.length > 0 && runParent === parent && runListId === listId && !!prevBlock && isAdjacentListItem(prevBlock, block);
            if (run.length && !continuesRun) flush();
            if (!run.length) {
                runParent = parent;
                runListId = listId;
            }
            const level = levelStr ? parseInt(levelStr, 10) : 1;
            const { text, isBulletFont } = extractOrdinalMarker(block);
            const type = classifyMarker(text, level, isBulletFont);
            run.push({ level, type, block, start: markerStartOrdinal(text, type) });
            prevBlock = block;
        }
        flush();

        stripConditionalRanges(body);

        return body.innerHTML;
    } catch {
        return html;
    }
}
